import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getUID, sendNotfication, isMobile, API } from '../Helper';
import CASINO_GAMES_CONFIG from '../config/casinoGamesConfig';

// ── Build a flat searchable game list from casinoGamesConfig ──
// Each game gets: id, title, vendor, game_icon, game_uid, supplier, source
const ALL_CONFIG_GAMES = CASINO_GAMES_CONFIG.flatMap(provider =>
  (provider.games || []).map(g => ({
    id: g.game_uid,
    game_uid: g.game_uid,
    uuid: g.game_uid,
    title: g.game_name,
    vendor: provider.name.toLowerCase(),
    providerCode: provider.code,
    providerName: provider.name,
    game_icon: g.image || '',
    image: g.image || '',
    supplier: g.supplier || provider.code,
    source: 'huidu',
  }))
);

// Styled components
const BackButton = styled.button`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1000;
  background: none;
  border: none;
  color: #fff;
  font-size: 24px;
  cursor: pointer;
  padding: 8px;
  
  &:hover {
    opacity: 0.8;
  }
`;

const FullScreenContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 999;
  background-color: #292d2e;
`;

const FullScreenIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  display: ${(props) => (props.loading ? 'none' : 'block')};
`;

// Context
const GameContext = createContext();

// Constants
const DEFAULT_PROVIDERS = ['spribe', 'pragmaticplay', 'netent', 'playtech', 'evolution'];

const GIS_PROVIDERS = [
  // GIS providers disabled - games now served via Huidu providers on home page
];

const VENDOR_DISPLAY_NAMES = {
  'amarix': 'Amarix',
  'amigogaming': 'AmigoGaming',
  'apollogames': 'ApolloGames',
  'apparat': 'Apparat',
  'aviatrix': 'Aviatrix',
  'bfgames': 'BFGames',
  'bgaming': 'BGaming',
  'barbarabang': 'BarbaraBang',
  'belatra': 'Belatra Games',
  'bigtimegaming': 'Big Time Gaming',
  'blueprint': 'Blueprint',
  'boldplay': 'Boldplay',
  'booongo': 'Booongo',
  'ctinteractive': 'CTInteractive',
  'caleta': 'Caleta',
  'clawbuster': 'Clawbuster',
  'conceptgaming': 'ConceptGaming',
  'dlv': 'Dlv',
  'dragoonsoft': 'Dragoon Soft',
  'elbet': 'Elbet',
  'endorphina': 'Endorphina',
  'enjoygaming': 'EnjoyGaming',
  'espressogames': 'Espressogames',
  'ezugi': 'Ezugi',
  'fbastards': 'FBastards',
  'fachai': 'FaChai',
  'formulaspin': 'FormulaSpin',
  'fugaso': 'Fugaso',
  'funkygames': 'FunkyGames',
  'gamebeat': 'Gamebeat',
  'hacksawgaming': 'Hacksaw Gaming',
  'hogaming': 'HoGaming',
  'iconic21': 'ICONIC21',
  'igrosoft': 'Igrosoft',
  'jdb': 'JDB',
  'jiligames': 'JiliGames',
  'kagaming': 'KAGaming',
  'kajotgames': 'KajotGames',
  'kalamba': 'Kalamba',
  'live88': 'Live88',
  'lottoinstantwin': 'Lotto Instant Win',
  'lucksome': 'Lucksome',
  'mplay': 'MPlay',
  'macawgaming': 'MacawGaming',
  'netent': 'NetEnt',
  'nolimitcity': 'No Limit City',
  'novomatic': 'Novomatic',
  'nucleus': 'Nucleus',
  'octoplay': 'Octoplay',
  'onetouch': 'OneTouch',
  'onlyplay': 'OnlyPlay',
  'orbitalgaming': 'OrbitalGaming',
  'penguinking': 'Penguin King',
  'peterandsons': 'PeterAndSons',
  'playhub': 'PlayHub',
  'playngo': 'PlayNGo',
  'playtech': 'Playtech',
  'popiplay': 'PopiPlay',
  'quickspin': 'Quickspin',
  'rtgslots': 'RTG SLOTS',
  'relaxgaming': 'Relax Gaming',
  'revolvergaming': 'RevolverGaming',
  'rich88': 'Rich88',
  'rivalgames': 'RivalGames',
  'rubyplay': 'RubyPlay',
  'sagaming': 'SA Gaming',
  'salsa': 'Salsa',
  'sevenmojos': 'SevenMojos Slots',
  'simpleplay': 'SimplePlay',
  'skywindslot': 'Skywind Slot',
  'smartbet': 'SmartBet',
  'spearheadstudios': 'Spearhead Studios',
  'spinmatic': 'Spinmatic',
  'spribe': 'Spribe',
  'superlottofast': 'SuperlottoFast',
  'superlottoslots': 'SuperlottoSlots',
  'superlottotv': 'SuperlottoTV',
  'tadagaming': 'TaDaGaming',
  'thunderkick': 'Thunderkick',
  'triplecherry': 'TripleCherry',
  'tripleprofitsgames': 'TripleProfitsGames',
  'turbogames': 'Turbogames',
  'vibragaming': 'VibraGaming',
  'vivogaming': 'Vivogaming',
  'wazdan': 'Wazdan',
  'xprogaming': 'XProgaming',
  'yggdrasil': 'Yggdrasil',
  'zeusplay': 'ZeusPlay',
  'zilliongames': 'Zillion Games'
};

// Default vendor keys that correspond to GIS providers
const DEFAULT_GIS_VENDOR_KEYS = Object.keys(VENDOR_DISPLAY_NAMES).filter(k =>
  GIS_PROVIDERS.includes(VENDOR_DISPLAY_NAMES[k])
);

const GAMES_PER_PAGE = 21;
const SEARCH_DEBOUNCE_MS = 300;
const CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

// Utility functions
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

const createCacheKey = (vendor, query, page) => {
  return `${vendor}_${query}_${page}`;
};

const isValidResponse = (json) => {
  return json && json.success && Array.isArray(json.data || json.payload?.games);
};

// Image preloading helper
const preloadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(src);
    img.onerror = reject;
    img.src = src;
  });
};

export const GameProvider = ({ children }) => {
  // Core state
  const [gameCache, setGameCache] = useState(new Map());
  const [imageCache, setImageCache] = useState(new Map());
  const [searchResults, setSearchResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [loadedProviders, setLoadedProviders] = useState(DEFAULT_PROVIDERS); // Start with default popular providers
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Provider loading configuration
  const BATCH_SIZE = 5; // Load 5 providers at a time
  const POPULAR_PROVIDERS = ['spribe', 'playtech', 'netent', 'bgaming', 'pragmaticplay']; // Priority providers
  
  // Queue for provider loading
  const providerQueueRef = useRef([]);
  const loadingProviderRef = useRef(false);
  const lastLoadTimeRef = useRef(Date.now());
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [iframeUrl, setIframeUrl] = useState('');
  const [showIframe, setShowIframe] = useState(false);
  const [showIframeLoading, setShowIframeLoading] = useState(false);
  
  // Search state
  const [currentQuery, setCurrentQuery] = useState('');
  const [selectedVendors, setSelectedVendors] = useState(DEFAULT_GIS_VENDOR_KEYS);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('name'); // name, vendor, type
  const [sortOrder, setSortOrder] = useState('asc'); // asc, desc
  const [gameType, setGameType] = useState('all'); // all, slots, table, live
  
  // Refs for optimization
  const abortControllerRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  
  // Memoized cache operations
  const getCachedData = useCallback((key) => {
    const cached = gameCache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_EXPIRY_MS) {
      return cached.data;
    }
    return null;
  }, [gameCache]);

  const setCachedData = useCallback((key, data) => {
    setGameCache(prev => new Map(prev).set(key, {
      data,
      timestamp: Date.now()
    }));
  }, []);

  // API functions
  const fetchGisGames = async (provider, query = '', page = 1, signal) => {
    console.log("Fetching games for provider:", provider);
    
    // Generate cache key
    const cacheKey = `gis_${provider}_${query}_${page}`;
    
    // Check memory cache first
    const cached = gameCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_EXPIRY_MS) {
      console.log("Cache hit for provider:", provider);
      return cached.data;
    }

    // Check localStorage cache as fallback
    const localCache = localStorage.getItem(cacheKey);
    if (localCache) {
      try {
        const parsed = JSON.parse(localCache);
        if (Date.now() - parsed.timestamp < CACHE_EXPIRY_MS) {
          console.log("LocalStorage cache hit for provider:", provider);
          setGameCache(prev => prev.set(cacheKey, parsed));
          return parsed.data;
        }
      } catch (e) {
        console.warn("Failed to parse localStorage cache:", e);
      }
    }
    
    const url = query.trim()
      ? `${API}/api/gis/gamesgis?provider=${encodeURIComponent(provider)}&search=${encodeURIComponent(query)}&page=${page}&limit=${GAMES_PER_PAGE}`
      : `${API}/api/gis/gamesgis?provider=${encodeURIComponent(provider)}&page=${page}&limit=${GAMES_PER_PAGE}`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const json = await response.json();
      
      if (!isValidResponse(json)) {
        throw new Error('Invalid response format');
      }
      
      const result = {
        games: json.data.map(game => ({
          id: game.uuid,
          title: game.name,
          vendor: (game.provider || provider).toLowerCase(),
          game_icon: game.image,
          game_type: game.type,
          uuid: game.uuid,
          source: 'gis',
          provider: provider
        })),
        pagination: json.pagination || { currentPage: page, totalPages: 1, total: json.data.length },
        hasMore: json.data.length === GAMES_PER_PAGE
      };
      
      // Cache in memory
      const cacheEntry = { data: result, timestamp: Date.now() };
      setGameCache(prev => prev.set(cacheKey, cacheEntry));
      
      // Cache in localStorage
      try {
        localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
      } catch (e) {
        console.warn("Failed to cache in localStorage:", e);
      }

      // Start preloading images
      result.games.forEach(game => {
        if (game.game_icon && !imageCache.has(game.game_icon)) {
          preloadImage(game.game_icon)
            .then(() => setImageCache(prev => prev.set(game.game_icon, true)))
            .catch(() => console.warn("Failed to preload image:", game.game_icon));
        }
      });
      
      return result;
    } catch (error) {
      console.error(`Failed to fetch games for provider ${provider}:`, error);
      throw error;
    }
  };

  const fetchJsGames = async (vendor, query = '', page = 1, signal) => {
    const response = await fetch(
      `${API}/jsGames/games/search?vendor=${encodeURIComponent(vendor)}&keyword=${encodeURIComponent(query)}&page=${page}&per_page=${GAMES_PER_PAGE}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal
      }
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const json = await response.json();
    
    if (!isValidResponse(json)) {
      throw new Error('Invalid response format');
    }
    
    return {
      games: json.payload.games.map(game => ({
        id: game.id,
        title: game.game_name,
        vendor: game.vendor.toLowerCase(),
        game_icon: game.game_icon,
        game_type: game.game_type,
        game_uid: game.game_uid,
        source: 'jsgames',
        provider: vendor
      })),
      pagination: json.payload.pagination || { currentPage: page, totalPages: 10 },
      hasMore: json.payload.games.length === GAMES_PER_PAGE
    };
  };

  const fetchGamesFromApi = async (vendor, query = '', page = 1, signal) => {
    const vendorName = VENDOR_DISPLAY_NAMES[vendor] || vendor;
    
    try {
      // Always use GIS endpoint regardless of provider
      return await fetchGisGames(vendorName, query, page, signal);
    } catch (error) {
      console.error(`Error fetching games for vendor ${vendor}:`, error);
      // Return empty result instead of throwing to allow other vendors to work
      return { 
        games: [], 
        pagination: { currentPage: page, totalPages: 1, total: 0 }, 
        hasMore: false 
      };
    }
  };

  // Search and filter logic
  const filterGames = useCallback((games, filters) => {
    let filtered = [...games];
    
    // Filter by game type
    if (filters.gameType && filters.gameType !== 'all') {
      filtered = filtered.filter(game => 
        game.game_type?.toLowerCase().includes(filters.gameType.toLowerCase())
      );
    }
    
    // Sort games
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (filters.sortBy) {
        case 'vendor':
          aValue = a.vendor || '';
          bValue = b.vendor || '';
          break;
        case 'type':
          aValue = a.game_type || '';
          bValue = b.game_type || '';
          break;
        default:
          aValue = a.title || '';
          bValue = b.title || '';
      }
      
      const comparison = aValue.localeCompare(bValue);
      return filters.sortOrder === 'desc' ? -comparison : comparison;
    });
    
    return filtered;
  }, []);

  const searchGames = useCallback(async (query = '', vendors = [], page = 1, filters = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      // Filter from local casinoGamesConfig data — no external API call
      let games = [...ALL_CONFIG_GAMES];

      // Filter by search query
      if (query.trim()) {
        const q = query.toLowerCase();
        games = games.filter(g =>
          g.title.toLowerCase().includes(q) ||
          g.providerName.toLowerCase().includes(q)
        );
      }

      // Filter by selected vendors (vendor keys from dropdown)
      if (vendors.length > 0) {
        const vendorSet = new Set(vendors.map(v => v.toLowerCase()));
        games = games.filter(g =>
          vendorSet.has(g.vendor.toLowerCase()) ||
          vendorSet.has(g.providerCode.toLowerCase())
        );
      }

      // Sort
      const sortField = filters.sortBy || sortBy;
      const sortDir = filters.sortOrder || sortOrder;
      games.sort((a, b) => {
        const av = sortField === 'vendor' ? a.providerName : a.title;
        const bv = sortField === 'vendor' ? b.providerName : b.title;
        return sortDir === 'desc' ? bv.localeCompare(av) : av.localeCompare(bv);
      });

      setSearchResults(games);
      setTotalCount(games.length);
      setHasMorePages(false);
      setCurrentPage(1);
      return games;
    } catch (err) {
      setError('Failed to search games');
      setSearchResults([]);
      setTotalCount(0);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [sortBy, sortOrder]);

  // Debounced search
  const debouncedSearch = useMemo(
    () => debounce((query, vendors, filters) => {
      searchGames(query, vendors, 1, filters);
    }, SEARCH_DEBOUNCE_MS),
    [searchGames]
  );

  // Load more function
  const loadMoreGames = useCallback(async () => {
    if (!hasMorePages || isLoading) return;
    
    try {
      // Use loadedProviders instead of selectedVendors to ensure we load from all available providers
      await searchGames(currentQuery, loadedProviders, currentPage + 1, {
        sortBy,
        sortOrder,
        gameType
      });
    } catch (error) {
      console.error('Load more error:', error);
      setError('Failed to load more games');
    }
  }, [hasMorePages, isLoading, currentQuery, loadedProviders, currentPage, searchGames, sortBy, sortOrder, gameType]);

  // Game launch functions
  const launchGisGame = async (game) => {
    const payload = {
      player_id: getUID(),
      player_name: localStorage.getItem('name') || 'Player',
      game_uuid: game.uuid,
      currency: localStorage.getItem('coin') || 'INR',
      device: isMobile() ? 'mobile' : 'desktop',
      return_url: `${API}/api/gis/callback/transactions`,
      language: 'en'
    };

    const response = await fetch(`${API}/api/gis/games/init`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    
    const json = await response.json();
    
    if (json.url) {
      setIframeUrl(json.url);
      setShowIframe(true);
      setShowIframeLoading(true);
    } else {
      throw new Error(json.message || 'Failed to launch game');
    }
  };

  const launchJsGame = async (game) => {
    const credits = localStorage.getItem('credit') || '0';
    const coins = localStorage.getItem('coin') || 'INR';
    
    if (!['USDT', 'INR'].includes(coins)) {
      throw new Error('Please swap into USDT to play');
    }

    const payload = {
      game_uid: game.game_uid,
      user_id: getUID(),
      credit_amount: credits,
      currency_code: coins,
      language: 'en'
    };

    const response = await fetch(`${API}/jsGamesv2/launch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    
    const json = await response.json();
    
    if (json.success && json.data?.game_launch_url) {
      setIframeUrl(json.data.game_launch_url);
      setShowIframe(true);
      setShowIframeLoading(true);
    } else {
      throw new Error(json.message || 'Failed to launch game');
    }
  };

  const handleGameLaunch = useCallback(async (game) => {
    const isLogged = localStorage.getItem('logged') === 'true';
    if (!isLogged) {
      sendNotfication('Please login to play', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const uid = getUID();
      const currency = localStorage.getItem('coin') || 'PKR';
      const res = await fetch(`${API}/huiduapi/game/play`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          game_uid: game.game_uid || game.uuid || game.id,
          user_id: uid,
          currency_code: currency,
          supplier_code: game.supplier || '',
          language: 'en',
        }),
      });
      const json = await res.json();
      if (json.success && json.data?.game_launch_url) {
        setIframeUrl(json.data.game_launch_url);
        setShowIframe(true);
        setShowIframeLoading(true);
      } else {
        throw new Error(json.error || 'Failed to launch game');
      }
    } catch (error) {
      console.error('Launch error:', error);
      setError(error.message || 'Failed to launch game');
      sendNotfication(error.message || 'Failed to launch game', 'error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Progressive provider loading with batching
  const loadNextProvider = useCallback(async () => {
    if (loadingProviderRef.current) return;
    
    const remainingProviders = DEFAULT_GIS_VENDOR_KEYS.filter(
      provider => !loadedProviders.includes(provider)
    );

    if (remainingProviders.length === 0) {
      setIsLoadingMore(false);
      return;
    }

    loadingProviderRef.current = true;
    
    // Take next batch of providers (up to 5)
    const nextBatch = remainingProviders.slice(0, 5);
    console.log('Loading next batch of providers:', nextBatch);

    try {
      // Load providers in parallel but with slight delays
      const loadPromises = nextBatch.map((provider, index) => 
        new Promise(resolve => {
          setTimeout(async () => {
            try {
              // Fetch provider games directly to avoid overwriting existing searchResults
              const apiResult = await fetchGamesFromApi(provider, '', 1);
              const newGames = (apiResult && apiResult.games) ? apiResult.games : [];

              // Preload images for this provider's games
              if (newGames.length) {
                const imagePromises = newGames
                  .map(game => game.game_icon ? preloadImage(game.game_icon).catch(() => null) : null)
                  .filter(Boolean);
                await Promise.allSettled(imagePromises);
              }

              // Append new games to current results with deduplication
              setSearchResults(prev => {
                const combined = [...prev, ...newGames];
                const unique = combined.reduce((acc, game) => {
                  const key = game.id || game.uuid || `${game.title}_${game.vendor}`;
                  if (!acc.has(key)) acc.set(key, game);
                  return acc;
                }, new Map());
                const arr = Array.from(unique.values());
                // Apply current filters and sorting
                try {
                  return filterGames(arr, { sortBy, sortOrder, gameType });
                } catch (e) {
                  return arr;
                }
              });

              // Update counts
              setTotalCount(prev => Math.max(prev || 0, apiResult.pagination?.total || newGames.length));
              setHasMorePages(prev => prev || Boolean(apiResult.hasMore));

              setLoadedProviders(prev => [...prev, provider]);
              resolve();
            } catch (error) {
              console.error(`Failed to load provider ${provider}:`, error);
              resolve(); // Continue with other providers even if one fails
            }
          }, index * 500); // 500ms stagger between each provider in the batch
        })
      );

      await Promise.all(loadPromises);
      
      // Queue next batch with delay
      setTimeout(() => {
        loadingProviderRef.current = false;
        loadNextProvider();
      }, 2000); // 2 second delay between batches
      
    } catch (error) {
      console.error('Failed while loading provider batch:', error);
      loadingProviderRef.current = false;
      // Try to continue with next batch
      setTimeout(() => loadNextProvider(), 500);
    }
  }, [loadedProviders, searchGames]);

  // Initialize — load all config games on mount
  useEffect(() => {
    searchGames('', [], 1, { sortBy: 'name', sortOrder: 'asc', gameType: 'all' });
  }, []);

  // Update search when filters change
  useEffect(() => {
    if (currentQuery || selectedVendors.length > 0) {
      debouncedSearch(currentQuery, selectedVendors, { sortBy, sortOrder, gameType });
    }
  }, [sortBy, sortOrder, gameType, debouncedSearch, currentQuery, selectedVendors]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleBackButton = useCallback(() => {
    setIframeUrl('');
    setShowIframe(false);
    setShowIframeLoading(false);
    window.location.reload();
  }, []);

  const handleIframeLoaded = useCallback(() => {
    setShowIframeLoading(false);
  }, []);

  // Function to start loading additional providers
  const startLoadingProviders = useCallback(() => {
    if (!loadingProviderRef.current) {
      loadNextProvider();
    }
  }, [loadNextProvider]);

  // Context value
  const contextValue = useMemo(() => ({
    // Data
    searchResults,
    totalCount,
    hasMorePages,
    loadedProviders,
    
    // State
    isLoading,
    error,
    currentQuery,
    selectedVendors,
    currentPage,
    sortBy,
    sortOrder,
    gameType,
    showIframe,
    isLoadingProviders: loadingProviderRef.current,
    
    // Actions
    searchGames: (query, vendors) => {
      setCurrentQuery(query);
      setSelectedVendors(vendors);
      debouncedSearch(query, vendors, { sortBy, sortOrder, gameType });
    },
    loadMoreGames,
    handleGameLaunch,
    setSortBy,
    setSortOrder,
    setGameType,
    setSelectedVendors,
    handleBackButton,
    clearError: () => setError(null),
    startLoadingProviders,
    
    // Constants — use casinoGamesConfig providers as vendor list
    vendorDisplayNames: Object.fromEntries(
      CASINO_GAMES_CONFIG.map(p => [p.code.toLowerCase(), p.name])
    ),
    gamesPerPage: GAMES_PER_PAGE,
    totalProviders: Object.keys(VENDOR_DISPLAY_NAMES).length
  }), [
    searchResults,
    totalCount,
    hasMorePages,
    isLoading,
    error,
    currentQuery,
    selectedVendors,
    currentPage,
    sortBy,
    sortOrder,
    gameType,
    showIframe,
    loadMoreGames,
    handleGameLaunch,
    debouncedSearch
  ]);

  if (showIframe) {
    return (
      <FullScreenContainer>
        {showIframeLoading && (
          <img
            src="assets/images/lg.png"
            alt="Loading"
            style={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)', 
              width: '250px' 
            }}
          />
        )}
        <FullScreenIframe
          src={iframeUrl}
          title="Game Iframe"
          onLoad={handleIframeLoaded}
          loading={showIframeLoading}
        />
        <BackButton onClick={handleBackButton}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </BackButton>
      </FullScreenContainer>
    );
  }

  return (
    <GameContext.Provider value={contextValue}>
      {children}
    </GameContext.Provider>
  );
};

export const useGames = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGames must be used within a GameProvider');
  }
  return context;
};
